/**
 * Presentation-only PWA update banner — ONE TRUE source. Each app's
 * thin wrapper owns its `useRegisterSW()` hook (because that's a
 * vite-plugin-pwa virtual module, only available inside the app build)
 * and passes the boolean + handlers in.
 *
 * Per [[feedback-pwa-update-prompt]]: visible "fresh version ready"
 * prompt instead of silent autoUpdate. Per [[feedback-unified-packages-ui]]:
 * extracted from 3 per-app copies (customer-pwa / dashboard / agent-pwa)
 * to honour the new rule "extract before the second copy lands" (here
 * already too late — three copies — but the third was the trigger).
 *
 * Placement variants match each app's form factor:
 *   - `bottom-center` — mobile-first; customer-pwa + agent-pwa
 *   - `bottom-right`  — desktop-first; dashboard
 *
 * Brand-safe via CSS variables. Defaults included so the banner still
 * renders when --color-* vars aren't loaded (e.g., NoTenantScreen).
 */

export interface PwaUpdateBannerProps {
  /** Render only when true. Caller passes `needRefresh` from useRegisterSW. */
  readonly needRefresh: boolean;
  /** Apply the new SW + reload. Caller's `updateServiceWorker(true)`. */
  readonly onApply: () => void;
  /** Hide for this session. Caller's `setNeedRefresh(false)`. */
  readonly onDismiss: () => void;
  /** Headline copy — keep custom-crafted per [[feedback-custom-crafted-ui-text]]. */
  readonly headline: string;
  /** Sub-copy below the headline. */
  readonly body: string;
  /** Primary CTA label (e.g., "Update now", "Reload now"). */
  readonly primaryLabel?: string;
  /** Secondary CTA label (e.g., "Later", "Not yet"). */
  readonly secondaryLabel?: string;
  /** Visual placement variant — mobile bottom-center vs desktop bottom-right. */
  readonly placement?: 'bottom-center' | 'bottom-right';
}

export function PwaUpdateBanner({
  needRefresh,
  onApply,
  onDismiss,
  headline,
  body,
  primaryLabel = 'Update now',
  secondaryLabel = 'Later',
  placement = 'bottom-center',
}: PwaUpdateBannerProps) {
  if (!needRefresh) return null;

  const positionStyle: React.CSSProperties =
    placement === 'bottom-right'
      ? {
          bottom: 'calc(var(--safe-bottom, 0px) + 24px)',
          right: 24,
          width: 380,
          maxWidth: 'calc(100vw - 48px)',
        }
      : {
          bottom: 'calc(var(--safe-bottom, 0px) + 16px)',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 'calc(100% - 32px)',
          maxWidth: 380,
        };

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        ...positionStyle,
        padding: 'var(--space-3, 12px) var(--space-4, 16px)',
        borderRadius: 'var(--radius-md, 12px)',
        background: 'var(--color-surface, #ffffff)',
        color: 'var(--color-on-surface, #0f172a)',
        boxShadow: 'var(--shadow-3, 0 12px 28px var(--color-border-strong))',
        border: '1px solid rgba(var(--color-primary-rgb), 0.22)',
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        alignItems: 'center',
        gap: 'var(--space-3, 12px)',
        zIndex: 50,
      }}
    >
      <div>
        <p style={{ margin: 0, fontWeight: 600, fontSize: 'var(--text-body, 14px)' }}>{headline}</p>
        <p
          style={{
            margin: 'var(--space-1, 4px) 0 0 0',
            fontSize: 'var(--text-meta, 12px)',
            color: 'var(--color-on-surface-muted, #64748b)',
            lineHeight: 1.4,
          }}
        >
          {body}
        </p>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1, 4px)' }}>
        <button
          type="button"
          onClick={onApply}
          style={{
            background: 'var(--color-primary, #475569)',
            color: 'var(--color-primary-fg, #ffffff)',
            border: 'none',
            padding: '10px 14px',
            borderRadius: 'var(--radius-sm, 8px)',
            fontWeight: 600,
            fontSize: 'var(--text-meta, 12px)',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            fontFamily: 'inherit',
          }}
        >
          {primaryLabel}
        </button>
        <button
          type="button"
          onClick={onDismiss}
          style={{
            background: 'transparent',
            color: 'var(--color-on-surface-muted, #64748b)',
            border: 'none',
            padding: '6px 10px',
            fontSize: 'var(--text-meta, 12px)',
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          {secondaryLabel}
        </button>
      </div>
    </div>
  );
}
